import {
  NumberInput,
  RadioButton,
  RadioButtonGroup,
  FormLabel,
  Layer,
} from '@carbon/react';
import style from './date-range-filter.module.scss';
import { useCallback, useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import {
  workingAbsoluteMaxDateAtom,
  workingAbsoluteMinDateAtom,
  workingDateRangeFilterTypeAtom,
  workingIntervalsPostAtom,
  workingIntervalsPreAtom,
} from '../../eval-3-workspace/atoms';
import { parseMomentUtc, SERVER_DATE_FORMAT } from 'utils/dates';
import MonthPicker from 'components/month-picker/month-picker';
import QuarterPicker from 'components/quarter-picker/quarter-picker';
import YearPicker from 'components/year-picker/year-picker';
import { AggregationPeriod } from 'types/misc';
import { isDateMidInterval } from './utils';
import { Moment } from 'moment';
import { DateFilterType } from 'types/evaluation';
import { capitalize } from 'lodash-es';
import classNames from 'classnames';

interface DateRangeFilterProps {
  packageId: number;
  aggregation: AggregationPeriod;
  effectiveDate: string;
}

/**
 * Date range filter component for controlling how the effective date range
 * is determined — either relative (intervals before/after) or absolute (specific dates).
 * Reads and writes working copy atoms scoped by packageId.
 *
 * @param {DateRangeFilterProps} props - Component props.
 * @return {JSX.Element} The date range filter.
 */
export const DateRangeFilter = ({
  packageId,
  aggregation,
  effectiveDate,
}: DateRangeFilterProps) => {
  const [rangeMin, setRangeMin] = useAtom(workingIntervalsPreAtom(packageId));
  const [rangeMax, setRangeMax] = useAtom(workingIntervalsPostAtom(packageId));

  const [absoluteMinDate, setAbsoluteMinDate] = useAtom(
    workingAbsoluteMinDateAtom(packageId)
  );
  const [absoluteMaxDate, setAbsoluteMaxDate] = useAtom(
    workingAbsoluteMaxDateAtom(packageId)
  );

  const [rangeType, setRangeType] = useAtom(
    workingDateRangeFilterTypeAtom(packageId)
  );

  // Ensure rangeType is initialized to 'relative' if not set
  useEffect(() => {
    if (!rangeType) {
      setRangeType('relative');
    }
  }, [rangeType, setRangeType]);

  const [localRange, setLocalRange] = useState<{
    min: number | '';
    max: number | '';
  }>({
    max: rangeMax,
    min: rangeMin,
  });

  const isMinValid =
    localRange.min === '' ||
    (Number.isInteger(localRange.min) && localRange.min > 0);
  const isMaxValid =
    localRange.max === '' ||
    (Number.isInteger(localRange.max) && localRange.max > 0);

  const isSplit = isDateMidInterval(effectiveDate, aggregation);

  const selectedMinDate = parseMomentUtc(
    absoluteMinDate ??
      parseMomentUtc(effectiveDate)
        .startOf(aggregation)
        .subtract(rangeMin, aggregation)
  );

  const selectedMaxDate = parseMomentUtc(
    absoluteMaxDate ??
      parseMomentUtc(effectiveDate)
        .endOf(aggregation)
        .add(rangeMax - (isSplit ? 0 : 1), aggregation)
  );

  const isSelectedMinDateValid = parseMomentUtc(selectedMinDate).isValid();
  const isSelectedMaxDateValid = parseMomentUtc(selectedMaxDate).isValid();

  const onRangeChange = (value: number | string, rangeType: 'min' | 'max') => {
    setLocalRange({
      ...localRange,
      [rangeType]: value === '' ? '' : Number(value),
    });
  };

  const setRange = useCallback(
    (range: { min: number; max: number }) => {
      setRangeMin(range.min);
      setRangeMax(range.max);
    },
    [setRangeMin, setRangeMax]
  );

  useEffect(() => {
    if (isMinValid && isMaxValid) {
      setRange({
        min: localRange.min === '' ? rangeMin : Number(localRange.min),
        max: localRange.max === '' ? rangeMax : Number(localRange.max),
      });
    }
  }, [isMinValid, isMaxValid, localRange, setRange, rangeMin, rangeMax]);

  useEffect(() => {
    if (isSelectedMinDateValid) {
      const formattedMinDate = parseMomentUtc(selectedMinDate)
        .startOf(aggregation)
        .format(SERVER_DATE_FORMAT);
      setAbsoluteMinDate(formattedMinDate);
    }
    if (isSelectedMaxDateValid) {
      const formattedMaxDate = parseMomentUtc(selectedMaxDate)
        .endOf(aggregation)
        .format(SERVER_DATE_FORMAT);
      setAbsoluteMaxDate(formattedMaxDate);
    }
  }, [
    aggregation,
    isSelectedMinDateValid,
    isSelectedMaxDateValid,
    selectedMinDate,
    selectedMaxDate,
    setAbsoluteMinDate,
    setAbsoluteMaxDate,
  ]);

  // Add this function to prevent wheel scrolling on number inputs
  const preventWheelChange = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
  };

  const intervalDatePicker = (rangeType: 'min' | 'max') => {
    const selectedDate =
      rangeType === 'min' ? selectedMinDate : selectedMaxDate;

    const setRange = (min: Moment, max: Moment) => {
      if (rangeType === 'min') {
        // Store only the formatted date string to avoid circular references
        const formattedDate = min.format(SERVER_DATE_FORMAT);
        setAbsoluteMinDate(formattedDate);
      } else {
        const formattedDate = max.format(SERVER_DATE_FORMAT);
        setAbsoluteMaxDate(formattedDate);
      }
    };

    // Prevent clicks from bubbling up to the radio button
    const handleDatePickerClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    if (aggregation === 'month') {
      return (
        <div className={style.datePicker} onClick={handleDatePickerClick}>
          <MonthPicker
            light
            selectedMonth={selectedDate}
            setSelectedMonth={(month, year) => {
              setRange(
                parseMomentUtc(`${year}-01-01`).month(month).startOf('month'),
                parseMomentUtc(`${year}-12-31`).month(month).endOf('month')
              );
            }}
            label={`${capitalize(rangeType)} Month`}
          />
        </div>
      );
    } else if (aggregation === 'quarter') {
      return (
        <div className={style.datePicker} onClick={handleDatePickerClick}>
          <QuarterPicker
            light
            selectedQuarter={selectedDate}
            setSelectedQuarter={(quarter, year) => {
              setRange(
                parseMomentUtc(`${year}-01-01`)
                  .quarter(quarter)
                  .startOf('quarter'),
                parseMomentUtc(`${year}-01-01`)
                  .quarter(quarter)
                  .endOf('quarter')
              );
            }}
            label={`${capitalize(rangeType)} Quarter`}
          />
        </div>
      );
    } else if (aggregation === 'year') {
      return (
        <div className={style.datePicker} onClick={handleDatePickerClick}>
          <YearPicker
            light
            selectedYear={selectedDate}
            setSelectedYear={(year) => {
              setRange(
                parseMomentUtc(`${year}-01-01`),
                parseMomentUtc(`${year}-12-31`)
              );
            }}
            label={`${capitalize(rangeType)} Year`}
          />
        </div>
      );
    }
    return undefined;
  };

  const aggregationLabel = capitalize(aggregation);

  const handleRangeTypeChange = (value: string | number | undefined) => {
    const safeValue = String(value) as DateFilterType;
    setRangeType(safeValue);
  };

  return (
    <div className={style.container}>
      <FormLabel className={style.rangeTypeLabel}>Range Type</FormLabel>
      <div className={style.columnsRow}>
        {/* Left column: Relative Range */}
        <div className={style.column}>
          <RadioButtonGroup
            name="range"
            valueSelected={rangeType}
            onChange={handleRangeTypeChange}
          >
            <RadioButton
              labelText="Relative Range"
              value="relative"
              id="relative-radio"
            />
          </RadioButtonGroup>

          <div
            className={classNames(style.inputsRow, {
              [style.disabled]: rangeType !== 'relative',
            })}
          >
            <Layer>
              <NumberInput
                label={`${aggregationLabel}s Before Effective Date`}
                invalid={!isMinValid}
                onChange={(_event, { value }) => onRangeChange(value, 'min')}
                min={0}
                invalidText="Positive integers are allowed"
                className={style.numberInput}
                size="sm"
                id="rangeMin"
                value={localRange.min}
                onWheel={preventWheelChange}
                disabled={rangeType !== 'relative'}
              />
            </Layer>
            <Layer>
              <NumberInput
                label={`${aggregationLabel}s After Effective Date`}
                invalid={!isMaxValid}
                onChange={(_event, { value }) => onRangeChange(value, 'max')}
                min={0}
                invalidText="Positive integers are allowed"
                className={style.numberInput}
                size="sm"
                id="rangeMax"
                value={localRange.max}
                onWheel={preventWheelChange}
                disabled={rangeType !== 'relative'}
              />
            </Layer>
          </div>
        </div>

        {/* Right column: Date Range */}
        <div className={style.column}>
          <RadioButtonGroup
            name="range"
            valueSelected={rangeType}
            onChange={handleRangeTypeChange}
          >
            <RadioButton
              labelText="Date Range"
              value="absolute"
              id="absolute-radio"
            />
          </RadioButtonGroup>

          <div
            className={classNames(style.inputsRow, {
              [style.disabled]: rangeType !== 'absolute',
            })}
          >
            {isSelectedMinDateValid && intervalDatePicker('min')}
            {isSelectedMaxDateValid && intervalDatePicker('max')}
          </div>
        </div>
      </div>
    </div>
  );
};
